import { useEffect } from 'react';

import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

import type { User } from '@sentry/react';

import * as Sentry from '@sentry/react';
import { z } from 'zod';

import { PAYMENT_REQUIRED_STATUS_CODE, UNAUTHORIZED_STATUS_CODE } from '@/shared/api/errors';
import { isOfType } from '@/shared/lib/validators/isOfType';

import { axiosSentryIntegration } from './lib/axios';
import { isButtonElement } from './lib/utils/isButtonElement';

const originalExceptionStatusSchema = z.object({ status: z.number() });
const originalExceptionResponseStatusSchema = z.object({ response: z.object({ status: z.number() }) });

const ERRORS: Record<PropertyKey, string> = {
  [PAYMENT_REQUIRED_STATUS_CODE]: '402_no_credits_available',
};

export const SentryRask = {
  initIfAvailable: () => {
    if (import.meta.env.VITE_SENTRY_DSN) {
      axiosSentryIntegration();
      Sentry.init({
        autoSessionTracking: true,
        beforeBreadcrumb: (breadcrumb, hint) => {
          if (breadcrumb.category?.includes('redux')) {
            return null;
          }

          if (breadcrumb.category?.startsWith('ui') && isButtonElement(hint)) {
            breadcrumb.message = `Button: ${hint?.event.target.textContent}`;
          }

          return breadcrumb;
        },
        beforeSend: (event, { originalException }) => {
          const statusFromDirect = isOfType(originalException, originalExceptionStatusSchema)
            ? originalException.status
            : undefined;
          const statusFromResponse = isOfType(originalException, originalExceptionResponseStatusSchema)
            ? originalException.response.status
            : undefined;
          const status = statusFromResponse ?? statusFromDirect;

          // 401 is expected (token refresh cycle) — drop silently
          if (status === UNAUTHORIZED_STATUS_CODE) {
            return null;
          }

          // Map known HTTP status codes to stable message strings
          if (event.type !== 'message' && status !== undefined && ERRORS[status]) {
            Sentry.captureMessage(ERRORS[status]);
            return null;
          }

          return event;
        },
        denyUrls: [
          /chrome-extension:\/\//i,
          /moz-extension:\/\//i,
          /safari-extension:\/\//i,
          /^file:\/\//,
        ],
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENV,
        ignoreErrors: [
          // Rewardful — blocked by adblockers / missing global
          /Rewardful is not defined/,
          /rewardful\.js.*Failed to load/i,
          // LogRocket — script load failures (adblock, network)
          /LogRocket.*Failed to fetch/i,
          /LogRocket.*blocked/i,
          // HubSpot — chat widget load failures, cookies blocked
          /HubSpot.*blocked/i,
          /_hsq is not defined/,
          // Telegram postEvent — fires when app is opened outside Telegram
          /Method not found/,
          // Browser noise — no actionable fix on our side
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          'Non-Error promise rejection captured',
          // ChunkLoadError — handled separately with reload, not sent to Sentry
          /Failed to fetch dynamically imported module/,
          /Loading chunk .* failed/,
        ],
        integrations: [
          Sentry.breadcrumbsIntegration({
            console: false,
          }),
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            networkDetailAllowUrls: ['https://rst.rask.ai/api/library/v1/media'],
            networkRequestHeaders: ['X-Custom-Header'],
            networkResponseHeaders: ['X-Custom-Header'],
          }),
          Sentry.reactRouterV6BrowserTracingIntegration({
            matchRoutes,
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
          }),
        ],
        normalizeDepth: 10,
        release: import.meta.env.VITE_RELEASE,
        replaysOnErrorSampleRate: 0.3,
        tracePropagationTargets: [
          'https://rst.rask.ai',
        ],
        tracesSampleRate: 0.02,
      });
    }
  },
  initUser: (user: User) => {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setUser(user);
    }
  },
  setTag: Sentry.setTag,
};
